import { Controller } from "@hotwired/stimulus"

/**
 * Decimal Input Controller - Handles locale-aware decimal number formatting
 *
 * Displays decimal numbers with a comma separator (e.g., 0,33) for user
 * readability, while normalizing back to dot notation (e.g., 0.33) on form
 * submit so the Rails server always receives a valid float string.
 *
 * Usage:
 *   <input type="text"
 *          data-controller="decimal-input"
 *          data-decimal-input-target="field" />
 *
 * The controller attaches to an individual <input> element. One controller
 * instance per field. The submit handler scans the entire form for ALL
 * decimal-input fields and normalizes each, so sibling fields controlled
 * by their own instances are not missed even when only one submit fires.
 */
export default class extends Controller {
  connect() {
    // Bind all handlers so the same function reference can be used for removal.
    this.onBlur = this.onBlur.bind(this)
    this.onPaste = this.onPaste.bind(this)
    this.normalizeForSubmit = this.normalizeForSubmit.bind(this)

    this.element.addEventListener('blur', this.onBlur)
    this.element.addEventListener('paste', this.onPaste)

    // Attach submit listener to the closest form so we can normalize before
    // Rails UJS serializes the form data.
    this._form = this.element.closest('form')
    if (this._form) {
      this._form.addEventListener('submit', this.normalizeForSubmit)
    }
  }

  disconnect() {
    this.element.removeEventListener('blur', this.onBlur)
    this.element.removeEventListener('paste', this.onPaste)

    if (this._form) {
      this._form.removeEventListener('submit', this.normalizeForSubmit)
      this._form = null
    }
  }

  // ---------------------------------------------------------------------------
  // Event handlers
  // ---------------------------------------------------------------------------

  /**
   * On blur: format the current value with a comma as the decimal separator.
   * Empty values are left untouched to avoid interfering with optional fields.
   */
  onBlur() {
    const raw = this.element.value.trim()
    if (raw === '') return

    const formatted = this.formatForDisplay(raw)
    if (formatted !== null) {
      this.element.value = formatted
    }
  }

  /**
   * On paste: wait one tick for the browser to commit the pasted text, then
   * apply the same display formatting as blur so the user sees a clean value.
   */
  onPaste() {
    setTimeout(() => {
      const raw = this.element.value.trim()
      if (raw === '') return

      const formatted = this.formatForDisplay(raw)
      if (formatted !== null) {
        this.element.value = formatted
      }
    }, 0)
  }

  /**
   * On form submit: find ALL decimal-input fields within the form and normalize
   * each value from display format (comma decimal) to server format (dot decimal)
   * before Rails UJS reads the form data.
   *
   * Iterating over all decimal-input fields rather than only `this.element`
   * guarantees correct behavior when the form holds multiple decimal fields,
   * because the first field's submit listener fires on behalf of all of them.
   *
   * @param {Event} event - The form submit event
   */
  normalizeForSubmit(event) {
    const form = event.currentTarget
    const decimalFields = form.querySelectorAll('[data-controller~="decimal-input"]')

    decimalFields.forEach((field) => {
      const raw = field.value.trim()
      if (raw === '') return

      const normalized = this.normalizeValue(raw)
      if (normalized !== null) {
        field.value = normalized
      }
    })
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /**
   * Detect the decimal separator used in a string and normalize it to a dot.
   *
   * Handles four common input patterns:
   *   1. Standard dot decimal:      "1234.56"  → "1234.56"
   *   2. Comma decimal, no sep:     "1234,56"  → "1234.56"
   *   3. Dot thousand, comma dec:   "1.234,56" → "1234.56"
   *   4. Comma thousand, dot dec:   "1,234.56" → "1234.56"
   *
   * Decision rule: when both separators appear, the one that comes LAST is the
   * decimal separator (the standard convention for European formatted numbers).
   * When only one separator appears AND it splits a group of exactly 3 digits
   * from the rest, it is treated as a thousand separator; otherwise it is the
   * decimal separator.
   *
   * @param {string} str - Raw string value from the input element
   * @returns {string|null} Dot-decimal string, or null if not parseable
   */
  normalizeValue(str) {
    if (str === '' || str == null) return null

    const lastDot   = str.lastIndexOf('.')
    const lastComma = str.lastIndexOf(',')

    let normalized

    if (lastDot !== -1 && lastComma !== -1) {
      // Both separators present — the one appearing last is the decimal sep.
      if (lastComma > lastDot) {
        // European format: 1.234,56
        normalized = str.replace(/\./g, '').replace(',', '.')
      } else {
        // US/UK format: 1,234.56
        normalized = str.replace(/,/g, '')
      }
    } else if (lastComma !== -1) {
      // Only comma present.
      // If exactly 3 digits follow the comma, it could be a thousand separator
      // (e.g., "1,234"), but we can't know for sure. We use the following
      // heuristic: if there are digits both before AND after and the part after
      // is exactly 3 digits with more than 1 digit before, treat it as thousand
      // separator; otherwise treat comma as decimal.
      const parts = str.split(',')
      const afterComma = parts[parts.length - 1]
      const beforeComma = parts.slice(0, -1).join('')

      if (parts.length === 2 && afterComma.length === 3 && /^\d+$/.test(afterComma) && beforeComma.length > 1) {
        // Ambiguous — likely a thousand separator (e.g., "1,234")
        // Treat as integer; remove comma.
        normalized = str.replace(/,/g, '')
      } else {
        // Decimal separator (e.g., "0,33" or "12,5")
        normalized = str.replace(',', '.')
      }
    } else if (lastDot !== -1) {
      // Only dot present — same ambiguity check as above.
      const parts = str.split('.')
      const afterDot = parts[parts.length - 1]
      const beforeDot = parts.slice(0, -1).join('')

      if (parts.length === 2 && afterDot.length === 3 && /^\d+$/.test(afterDot) && beforeDot.length > 1) {
        // Likely a thousand separator (e.g., "1.234")
        normalized = str.replace(/\./g, '')
      } else {
        // Standard dot decimal — leave as is.
        normalized = str
      }
    } else {
      // No separator at all — plain integer string.
      normalized = str
    }

    // Final guard: only return if the result is a valid finite number.
    const parsed = parseFloat(normalized)
    if (isNaN(parsed)) return null

    return normalized
  }

  /**
   * Parse the raw value and return a display string with comma as the decimal
   * separator.  Returns null when the value cannot be interpreted as a number.
   *
   * @param {string} str - Raw string value (may already use comma or dot)
   * @returns {string|null} Display string like "1234,56", or null on failure
   */
  formatForDisplay(str) {
    const normalized = this.normalizeValue(str)
    if (normalized === null) return null

    const parsed = parseFloat(normalized)
    if (isNaN(parsed)) return null

    // Preserve the precision from the original input rather than forcing a
    // fixed number of decimal places, so "0.3" stays "0,3" and not "0,30".
    const dotIndex = normalized.indexOf('.')
    if (dotIndex === -1) {
      // Whole number — no decimal part to display.
      return String(parsed)
    }

    const decimalPlaces = normalized.length - dotIndex - 1
    return parsed.toFixed(decimalPlaces).replace('.', ',')
  }
}
